/**
 * explosion_fx.ts — the pieces of a STAGED ground explosion (fire realism R5, 2026-09-25), shared by every
 * CombatVfx (battles, war air strikes, crashes, the hovercraft's cannon). Production explosions are staged —
 * flash, fireball, shockwave, debris, smoke that lives on, secondaries — not a glow blob:
 *
 *   FLASH      a broker light (the fire module's shared pool: 25 m, 150 ms, 5,000 cd × scale²)
 *   FIREBALL   the fire shader's fireball (lib/vfx/fire_volume.ts) — expands, cools, becomes its own smoke
 *   SHOCKWAVE  a PLANAR ring on the ground (the old ring was a camera-facing sprite: it turned with you and never
 *              read as a ring on the ground) + a DUST SKIRT thrown outward + a heat-haze band at high+
 *   DEBRIS     6–14 chunks from ONE instanced pool (a fixed count — the cache-key law; dead slots scale to 0),
 *              glowing hot and cooling, trailing embers into the fire system's ember emitter
 *   SMOKE      a smoke-only 'fire' that lingers 10–20 s (the six-way column on WebGPU, smoke cards elsewhere)
 *   SCORCH     the existing 'blast' decal
 *   AFTER-FIRE a small fire that burns 20–60 s where a wreck-sized blast (scale ≥ 2.5) went off
 * Per-piece state rides per-OBJECT uniforms (`onObjectUpdate`), so every ring / skirt shares one material and
 * one pipeline (the material-identity law). All overlays `markMrtOverlay`.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, texture3D, smoothstep, saturate, length, positionView,
  cameraNear, cameraFar, mix, pow, attribute, positionLocal, positionPrevious,
} from 'three/tsl';
import { vfxSceneLinearDepth } from './scene_depth.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { createHeatHazeMaterial } from 'enginex/vfx/HeatHazeMaterial.js';
import { fireTextures, uSmokeLight, uBlastEmission, uFireEmission, hazeSceneColor } from './fire_volume.js';
import { reportBlast } from '../damage_hooks.js'; // shader gaps R2: a staged blast leaves its crater / stripped wall

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;

function objVec4(key: string): N {
  const fallback = new THREE.Vector4();
  const read = (frame: { object: THREE.Object3D | null }): THREE.Vector4 => (frame.object?.userData[key] as THREE.Vector4 | undefined) ?? fallback;
  return uniform(new THREE.Vector4()).onObjectUpdate(read as never);
}
const softFade = (dist: number): N => {
  const sceneDepth = vfxSceneLinearDepth.mul(cameraFar.sub(cameraNear)).add(cameraNear); // the VFX's own snapshot (scene_depth.ts)
  return saturate(sceneDepth.sub(positionView.z.negate()).div(dist));
};
function premultOver(m: THREE.NodeMaterial): void {
  m.transparent = true; m.depthWrite = false; m.fog = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor; m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
}

// ── The planar shockwave ring ───────────────────────────────────────────────
let ringGeo: THREE.PlaneGeometry | null = null, ringMat: THREE.NodeMaterial | null = null;
/** A flat unit quad on the ground (XZ) — scale it to the ring's diameter. */
export function shockRingGeometry(): THREE.PlaneGeometry {
  if (!ringGeo) { ringGeo = new THREE.PlaneGeometry(1, 1); ringGeo.rotateX(-Math.PI / 2); }
  return ringGeo;
}
/**
 * userData.ring = (age 0..1, seed, heat 0..1, unused): a hot white-orange front that cools to a dusty grey band,
 * thinning as it spreads, broken by the noise, fading out.
 */
export function shockRingMaterial(): THREE.NodeMaterial {
  if (ringMat) return ringMat;
  const R = objVec4('ring');
  const m = new THREE.NodeMaterial();
  m.name = 'explosion-ring';
  m.side = THREE.DoubleSide;
  premultOver(m);
  m.colorNode = Fn(() => {
    const age = R.x, seed = R.y, heat = R.z;
    const d = uv().sub(0.5).mul(2);
    const r = length(d);
    const width = mix(float(0.22), float(0.06), age);
    const band = smoothstep(float(1).sub(width), float(0.97), r).mul(float(1).sub(smoothstep(0.97, 1.0, r)));
    const n = texture3D(fireTextures().noise, vec3(d.x.mul(1.3).add(seed), d.y.mul(1.3), age.mul(0.6))).w;
    const breakup = saturate(n.mul(1.8).sub(0.25));
    const fade = pow(float(1).sub(age), float(1.6));
    // §4.3 (surfaces, 2026-09-27): the flash front on the curve (uBlastEmission: 50 kcd/m², held at display 3).
    const hot = vec3(6.0, 3.2, 1.2).mul(heat).mul(pow(float(1).sub(age), float(3))).mul(uBlastEmission);
    const dustA = band.mul(breakup).mul(fade).mul(0.55);
    const rgb = hot.mul(band).mul(breakup).add(uSmokeLight.mul(1.4).mul(dustA));
    const f = softFade(0.35);
    return vec4(rgb.mul(f), dustA.mul(f));
  })();
  m.positionNode = Fn(() => { positionPrevious.assign(positionLocal); return positionLocal; })();
  markMrtOverlay(m);
  ringMat = m;
  return m;
}

// ── The heat-haze disc over the blast (high+, the fire haze's gate) ──────────
let hazeMat: { material: THREE.NodeMaterial; uniforms: { strength: { value: number } } } | null = null;
/** One shared refraction material (the engine's HeatHazeMaterial: a noise-offset scene-colour fetch, radially masked). */
export function blastHazeMaterial(): { material: THREE.NodeMaterial; uniforms: { strength: { value: number } } } {
  if (!hazeMat) {
    const h = createHeatHazeMaterial({ strength: 0.014, noiseScale: 7, speed: 3, edgeSoftness: 0.6, sceneColor: hazeSceneColor() as never }); // surfaces (2026-09-27): THE HAZE COPY
    h.material.name = 'explosion-haze';
    markMrtOverlay(h.material);
    hazeMat = h as never;
  }
  return hazeMat!;
}

// ── The dust skirt ──────────────────────────────────────────────────────────
let skirtGeo: THREE.CylinderGeometry | null = null, skirtMat: THREE.NodeMaterial | null = null;
/** An open unit cylinder (radius 1 → 1.15 at the top, y 0..1) — scale x/z to the skirt's radius, y to its height. */
export function dustSkirtGeometry(): THREE.CylinderGeometry {
  if (!skirtGeo) { skirtGeo = new THREE.CylinderGeometry(1.15, 1, 1, 28, 1, true); skirtGeo.translate(0, 0.5, 0); }
  return skirtGeo;
}
/** userData.skirt = (age 0..1, seed, darkness 0..1, unused): billowing ground dust, densest low, thrown outward. */
export function dustSkirtMaterial(): THREE.NodeMaterial {
  if (skirtMat) return skirtMat;
  const S = objVec4('skirt');
  const m = new THREE.NodeMaterial();
  m.name = 'explosion-dust-skirt';
  m.side = THREE.DoubleSide;
  premultOver(m);
  m.colorNode = Fn(() => {
    const age = S.x, seed = S.y, dark = S.z;
    const u0 = uv();
    const n = texture3D(fireTextures().noise, vec3(u0.x.mul(3).add(seed), u0.y.mul(0.9).sub(age.mul(0.7)), seed.mul(0.3).add(age.mul(0.2)))).w;
    const low = pow(float(1).sub(u0.y), float(1.6));
    const dens = saturate(n.mul(2).sub(0.55)).mul(low).mul(pow(float(1).sub(age), float(1.3)));
    const a = dens.mul(0.7).mul(softFade(0.8));
    const col = mix(vec3(0.62, 0.55, 0.45), vec3(0.2, 0.18, 0.16), dark).mul(uSmokeLight.mul(2.2));
    return vec4(col.mul(a), a);
  })();
  m.positionNode = Fn(() => { positionPrevious.assign(positionLocal); return positionLocal; })();
  markMrtOverlay(m);
  skirtMat = m;
  return m;
}

// ── Debris ──────────────────────────────────────────────────────────────────
export const DEBRIS_POOL = 64;
export interface DebrisPool {
  mesh: THREE.InstancedMesh;
  heat: THREE.InstancedBufferAttribute;
  live: Array<{ i: number; p: THREE.Vector3; v: THREE.Vector3; spin: THREE.Vector3; rot: THREE.Euler; size: number; age: number; life: number; hot: number; rest: boolean }>;
  free: number[];
}
/**
 * ONE instanced pool of rock/metal chunks (a fixed count: the cache-key law — dead slots are scaled to 0), a lit
 * material whose emission is the per-instance HEAT (a chunk thrown out of a fireball glows and cools). Vertex
 * buffers: position, normal, the instance matrix (+ its previous copy with velocity on), aHeat — five or six.
 * `pinStaticMotionVectors`: r185 never re-uploads previous instance matrices, so the chunks carry no velocity
 * (motion blur is off on every stock tier; better no streak than a frozen one).
 */
export function createDebrisPool(): DebrisPool {
  const geo = new THREE.DodecahedronGeometry(0.5, 0);
  geo.deleteAttribute('uv');
  const heat = new THREE.InstancedBufferAttribute(new Float32Array(DEBRIS_POOL), 1);
  heat.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aHeat', heat);
  const mat = new THREE.MeshStandardNodeMaterial({ color: 0x3a3632, roughness: 0.92, metalness: 0.08 });
  mat.name = 'explosion-debris';
  mat.emissiveNode = Fn(() => {
    const h = attribute('aHeat', 'float');
    // §4.3 (surfaces, 2026-09-27): a hot chunk glows like an ember — the fire's own radiance scale (uFireEmission).
    return vec3(4.0, 1.3, 0.25).mul(pow(h, float(2.2))).mul(uFireEmission);
  })();
  pinStaticMotionVectors(mat as never);
  const mesh = new THREE.InstancedMesh(geo, mat, DEBRIS_POOL);
  mesh.name = 'explosion-debris';
  mesh.count = DEBRIS_POOL;
  mesh.frustumCulled = false;
  mesh.castShadow = true;
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  for (let i = 0; i < DEBRIS_POOL; i++) mesh.setMatrixAt(i, zero);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.visible = false; // PERF (shader sweep C, 2026-10-06): drawn only while a chunk flies — see stepDebris
  return { mesh, heat, live: [], free: Array.from({ length: DEBRIS_POOL }, (_, i) => DEBRIS_POOL - 1 - i) };
}

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3();
/** Throw `n` chunks from an ABSOLUTE point (the pool's mesh is a scene child: its instances are absolute). */
export function throwDebris(pool: DebrisPool, p: THREE.Vector3, n: number, scale: number): void {
  reportBlast(p.x, p.y, p.z, scale); // once per staged explosion (CombatVfx throws its debris exactly once) — before the pool can run dry
  for (let k = 0; k < n; k++) {
    const i = pool.free.pop();
    if (i === undefined) return;
    const a = Math.random() * Math.PI * 2, up = 0.45 + Math.random() * 0.55, sp = (5 + Math.random() * 9) * Math.sqrt(scale);
    const size = (0.12 + Math.random() * 0.22) * Math.min(2.2, scale);
    pool.live.push({
      i, p: p.clone(), v: new THREE.Vector3(Math.cos(a) * sp * (1 - up * 0.6), sp * up + 2, Math.sin(a) * sp * (1 - up * 0.6)),
      spin: new THREE.Vector3((Math.random() - 0.5) * 12, (Math.random() - 0.5) * 12, (Math.random() - 0.5) * 12), rot: new THREE.Euler(),
      size, age: 0, life: 7 + Math.random() * 5, hot: 0.7 + Math.random() * 0.3, rest: false,
    });
    pool.mesh.visible = true;
  }
}
/**
 * Integrate the chunks: gravity, a bounce off the ground (`groundAt`, absolute), rest; the heat cools over ~2.5 s;
 * a hot, moving chunk calls `trail(x, y, z)` (the ember streak). Dead chunks free their slot at scale 0.
 */
export function stepDebris(pool: DebrisPool, dt: number, groundAt: (x: number, z: number) => number, trail: (x: number, y: number, z: number) => void): void {
  // PERF (shader sweep C, 2026-10-06): the pool used to stay in the scene after the session's first blast — 64
  // zero-scaled chunks drawn in the scene and every shadow pass, and (Dynamic usage) their whole matrix + heat buffers
  // re-uploaded every frame, forever. Hidden while nothing flies; a throw shows it again.
  if (!pool.live.length) { pool.mesh.visible = false; return; }
  for (let k = pool.live.length - 1; k >= 0; k--) {
    const c = pool.live[k];
    c.age += dt;
    if (c.age > c.life) {
      _m.makeScale(0, 0, 0); pool.mesh.setMatrixAt(c.i, _m); pool.heat.setX(c.i, 0);
      pool.free.push(c.i); pool.live.splice(k, 1);
      continue;
    }
    if (!c.rest) {
      c.v.y -= 9.81 * dt;
      c.p.addScaledVector(c.v, dt);
      c.rot.x += c.spin.x * dt; c.rot.y += c.spin.y * dt; c.rot.z += c.spin.z * dt;
      const g = groundAt(c.p.x, c.p.z) + c.size * 0.4;
      if (c.p.y < g) {
        c.p.y = g;
        if (Math.abs(c.v.y) < 1.2) { c.rest = true; c.v.set(0, 0, 0); }
        else { c.v.y = -c.v.y * 0.32; c.v.x *= 0.55; c.v.z *= 0.55; c.spin.multiplyScalar(0.5); }
      }
      if (c.age < 1.6 && Math.random() < 0.55) trail(c.p.x, c.p.y, c.p.z);
    }
    const heat = c.hot * Math.max(0, 1 - c.age / 2.6);
    const shrink = c.age > c.life - 1 ? Math.max(0, c.life - c.age) : 1;
    _q.setFromEuler(c.rot);
    _s.setScalar(c.size * shrink);
    _m.compose(c.p, _q, _s);
    pool.mesh.setMatrixAt(c.i, _m);
    pool.heat.setX(c.i, heat);
  }
  pool.mesh.instanceMatrix.needsUpdate = true;
  pool.heat.needsUpdate = true;
}
